/*
 * PLAYXWORLD ABOUT PAGE — MONOCHROME BRUTALISM
 * Brand story, mission, and team info in editorial layout
 */
import Layout from "@/components/Layout";
import { motion } from "framer-motion";
import { useInView } from "@/hooks/useInView";
import { useTranslation } from "react-i18next";

const TOPUP_IMG = "https://private-us-east-1.manuscdn.com/sessionFile/ensg2s7EtdwrKwwyBUN9yG/sandbox/MUeFljqOndb6xQbGvjf4Gf-img-3_1771390254000_na1fn_dG9wdXAtc2VjdGlvbg.png?x-oss-process=image/resize,w_1920,h_1920/format,webp/quality,q_80&Expires=1798761600&Policy=eyJTdGF0ZW1lbnQiOlt7IlJlc291cmNlIjoiaHR0cHM6Ly9wcml2YXRlLXVzLWVhc3QtMS5tYW51c2Nkbi5jb20vc2Vzc2lvbkZpbGUvZW5zZzJzN0V0ZHdyS3d3eUJVTjl5Ry9zYW5kYm94L01VZUZsanFPbmRiNnhRYkd2amY0R2YtaW1nLTNfMTc3MTM5MDI1NDAwMF9uYTFmbl9kRzl3ZFhBdGMyVmpkR2x2YmcucG5nP3gtb3NzLXByb2Nlc3M9aW1hZ2UvcmVzaXplLHdfMTkyMCxoXzE5MjAvZm9ybWF0LHdlYnAvcXVhbGl0eSxxXzgwIiwiQ29uZGl0aW9uIjp7IkRhdGVMZXNzVGhhbiI6eyJBV1M6RXBvY2hUaW1lIjoxNzk4NzYxNjAwfX19XX0_&Key-Pair-Id=K2HSFNDJXOU9YS&Signature=B4qt~pn03WcewUZonZwggyIgDlnsUrzNVZBwFzJIDjDAwgyM6CXY8cwTuZDL-SkyFkmm0R9BMH6iC-om4jtNFeKOEx7f~A9pEJby3zdsj30baQavqEr~7haIOFI~wVF4K2UBVKT3lg5HnifWI9KlYkc6XknW9oXMDp61o-DHdk3QcKFOglch1BtQvoJtwfD0XK2FP4d6DHs-7tqT5RAp8X3Ee0K3FC5fGPCW7X8nXDILq5KoGpF9KWQs0pIMEnuXMUjkM6Is9XJSABNRUowgrXvxlnDmAjYWhXY5dLIQ0pTPthD0oIjCCTrJE3IB2zB-v6ekv5bh-onHmH2w-1wWcQ__";

export default function About() {
  const { t } = useTranslation();
  const { ref: missionRef, isInView: missionInView } = useInView(0.1);
  const { ref: statsRef, isInView: statsInView } = useInView(0.1);

  const values = [
    { title: t("about.values.speed.title"), desc: t("about.values.speed.desc") },
    { title: t("about.values.security.title"), desc: t("about.values.security.desc") },
    { title: t("about.values.transparency.title"), desc: t("about.values.transparency.desc") },
    { title: t("about.values.care.title"), desc: t("about.values.care.desc") },
  ];

  const stats = [
    { num: "50,000+", label: t("about.stats.users") },
    { num: "100+", label: t("about.stats.games") },
    { num: "1M+", label: t("about.stats.transactions") },
    { num: "99.9%", label: t("about.stats.satisfaction") },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-white text-black border-b-3 border-black">
        <div className="container py-16 md:py-24">
          <span className="font-mono text-xs tracking-widest text-black/50">"ABOUT US"</span>
          <h1 className="font-heading font-bold text-5xl md:text-6xl lg:text-7xl tracking-tight mt-2 max-w-3xl">
            {t("about.title")}
          </h1>
          <p className="text-black/60 text-lg mt-4 max-w-2xl leading-relaxed">
            {t("about.subtitle")}
          </p>
        </div>
      </section>

      {/* Brand Image */}
      <section className="bg-black">
        <div className="h-64 md:h-96 overflow-hidden">
          <img
            src={TOPUP_IMG}
            alt="PlayXworld"
            className="w-full h-full object-cover opacity-60"
            loading="lazy"
          />
        </div>
      </section>

      {/* Mission */}
      <section ref={missionRef} className="bg-white text-black py-16 md:py-24">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            <motion.div
              initial={{ x: -60, opacity: 0 }}
              animate={missionInView ? { x: 0, opacity: 1 } : {}}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="font-mono text-xs tracking-widest text-black/50">"OUR MISSION"</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mt-2 mb-6">
                {t("about.missionTitle")}
              </h2>
              <div className="space-y-4 text-black/70 leading-relaxed">
                <p>
                  <strong>{t("about.missionLead")}</strong>
                </p>
                <p>{t("about.missionP1")}</p>
                <p>{t("about.missionP2")}</p>
                <p>{t("about.missionP3")}</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ x: 60, opacity: 0 }}
              animate={missionInView ? { x: 0, opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="font-mono text-xs tracking-widest text-black/50">"OUR VALUES"</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight mt-2 mb-6">
                {t("about.valuesTitle")}
              </h2>
              <div className="space-y-4">
                {values.map((value) => (
                  <div key={value.title} className="border-l-4 border-black pl-4">
                    <h3 className="font-heading font-bold text-lg">{value.title}</h3>
                    <p className="text-black/60 text-sm">{value.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="bg-black text-white py-16 md:py-24">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ y: 40, opacity: 0 }}
                animate={statsInView ? { y: 0, opacity: 1 } : {}}
                transition={{ duration: 0.4, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="text-center"
              >
                <div className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl mb-2">
                  {stat.num}
                </div>
                <div className="font-mono text-xs tracking-widest text-white/50">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
